import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Bot,
  CalendarClock,
  ClipboardList,
  Gauge,
  GraduationCap,
  LogOut,
  MessagesSquare,
  Pin,
  PinOff,
  Route,
  Settings,
  ShieldAlert,
  X,
} from "lucide-react";
import Tooltip from "../design/Shared/Tooltip";
import "./AgentRail.css";
import greenRoadIcon from "../../images/greenroad-icon.svg";
import greenRoadLogo from "../../images/greenroad-logo.svg";
import { useAgentAccess } from "../context/AgentAccessContext";
import { isMenuDisabled } from "../../business/helper/Singleton";

export const ASKMILA_KEY = "askmila";

const TOOLTIP_ID = "tooltipAgentRail";

/** The strip the rail rests as, and the width it opens to — whether peeking or pinned. */
const RAIL_CLOSED_WIDTH = "w-[64px]";
const RAIL_OPEN_WIDTH = "w-[236px]";

/**
 * The open panel's content width — the open rail less its own horizontal padding (`px-2.5`).
 *
 * The header row is laid out at this width in both states and clipped when the rail is closed,
 * which is what holds the pin perfectly still while the panel opens: it fades in where it
 * already was, rather than being re-laid-out and sliding right, out from under the pointer
 * travelling towards it. The entries do the same thing for their labels a little lower down.
 */
const RAIL_CONTENT_WIDTH = "w-[216px]";

/**
 * The pin is a preference rather than a session detail: someone who pins the rail open meant
 * it, and re-pinning after every reload would be its own small irritation.
 *
 * Wrapped because `localStorage` throws outright in some embedded contexts instead of returning
 * null, and a rail that forgets is a far better outcome than a rail that cannot render.
 */
const PIN_STORAGE_KEY = "milaAiRailPinned";

const readPinned = () => {
  try {
    return window.localStorage.getItem(PIN_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writePinned = (isPinned) => {
  try {
    window.localStorage.setItem(PIN_STORAGE_KEY, String(isPinned));
  } catch {
    /* A forgotten preference is not worth failing a click over. */
  }
};

/**
 * Icons by `iconKey`, which the registry row supplies. The set is deliberately about the
 * nature of the work — a shield prevents, a route plans, a cap coaches — so a closed rail
 * reads without labels. An unknown key falls back to a generic agent mark rather than
 * rendering nothing, so an agent shipped backend-first still gets a usable rail entry.
 */
const ICONS = {
  "shield-alert": ShieldAlert,
  route: Route,
  "graduation-cap": GraduationCap,
  gauge: Gauge,
  "calendar-clock": CalendarClock,
  "clipboard-list": ClipboardList,
  bot: Bot,
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * One top-level entry.
 *
 * Its geometry is identical open and closed — the same 44px row, the same icon column, the
 * same spacing. Only the label fades. That matters more now that the rail opens on hover than
 * it did behind a click: anything that moved vertically would slide out from under the very
 * pointer that opened the rail.
 */
const RailItem = ({
  icon: Icon,
  label,
  description,
  badge,
  isActive,
  isOpen,
  onClick,
  testId,
  /* Settings and sign-out use this: both inherit "not while a response is streaming" from the
     controls they replaced. Navigation entries stay clickable mid-stream on purpose —
     switching views does not destroy the conversation, signing out does. */
  disabled = false,
  /* A moment's pulse, to draw the eye — the settings entry wears it right after onboarding,
     so the user sees where the preferences Mila just learned can be changed. */
  isHighlighted = false,
}) => {
  /* The description alone. The name is already on screen whenever the tooltip can appear: it
     is the pointer resting here that opened the rail. */
  const tip = description ? escapeHtml(description) : undefined;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-current={isActive ? "page" : undefined}
      aria-label={label}
      /* Attached only when there is something to say: an empty `content` still renders an
         empty tooltip card. */
      {...(tip ? { "data-tooltip-id": TOOLTIP_ID, "data-tooltip-content": tip } : {})}
      data-tooltip-place="right"
      className={clsx(
        "relative flex min-h-[44px] w-full items-center overflow-hidden rounded-2xl font-notosans text-[14px] transition-colors duration-200",
        isActive
          ? "bg-blue-200 font-bold text-dark-blue-100"
          : disabled
            ? "cursor-not-allowed text-dimgray-200 opacity-50"
            : "text-dimgray-200 hover:bg-whitesmoke-600 hover:text-dark-blue-100",
        /* The same pulse the sidebar gear wore, drawn inside the row so it stays within the
           closed strip rather than spilling over the rail's edge. */
        isHighlighted &&
          "animate-pulse bg-[#F3F1FF] text-dark-blue-100 ring-2 ring-inset ring-[#C4C2F4]"
      )}
    >
      {/* The marker is the one AskMila already uses for a selected conversation: a navy bar on
          the left edge of the panel, so the rail reads as the same product. */}
      <span
        className={clsx(
          "absolute left-[-10px] top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-[#2A354A] transition-opacity duration-200",
          isActive ? "opacity-100" : "opacity-0"
        )}
      />

      {/* A fixed 44px icon slot in both states, so the icons do not drift sideways while the
          rail opens: the labels slide out from behind them. */}
      <span className="grid h-11 w-11 shrink-0 place-items-center">
        <Icon size={19} strokeWidth={isActive ? 2.1 : 1.7} />
      </span>

      {/* Not a flex row: the badge flows with the name so a long agent name wraps to a second
          line and the badge lands after the last word, rather than squeezing the name.

          A fixed width rather than `flex-1`, and it is the open rail it is measured for
          (236px, less the padding and the icon): the name has to wrap the same way in both
          states or the row is a different height in each, and the entries below it would jump
          up the moment the rail opened — out from under the very pointer that opened it. It
          overhangs the closed rail by design, and the row clips it. */}
      <span
        className={clsx(
          "w-[172px] shrink-0 pr-3 text-left leading-5 transition-opacity duration-200",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        {label}
        {badge && (
          <span className="ml-1.5 inline-block whitespace-nowrap rounded-full bg-white px-1.5 py-px align-[2px] text-[9px] font-bold uppercase tracking-wider text-dimgray-400">
            {badge}
          </span>
        )}
      </span>
    </button>
  );
};

/**
 * A section heading. Closed, the group needs a separator rather than a word — so the two are
 * stacked in one 16px row and crossfaded, rather than swapped for one another and taking the
 * rest of the rail up or down with them.
 */
const RailSection = ({ label, isOpen, className }) => (
  <div className={clsx("relative flex h-4 items-center", className)}>
    <span
      aria-hidden="true"
      className={clsx(
        "absolute inset-x-2 h-px bg-whitesmoke-500 transition-opacity duration-200",
        isOpen ? "opacity-0" : "opacity-100"
      )}
    />
    <span
      className={clsx(
        "truncate pl-3 font-notosans text-[10px] font-bold uppercase tracking-[0.14em] text-dimgray-400 transition-opacity duration-200",
        isOpen ? "opacity-100" : "opacity-0"
      )}
    >
      {label}
    </span>
  </div>
);

/**
 * The left-hand rail of AI services (design §13.1, PRD "How it is navigated").
 *
 * A rail rather than tabs across the top, because it is the pattern the whole portfolio
 * inherits: it has to hold ten entries rather than two, and top tabs stop working at that
 * point and start reading like browser tabs.
 *
 * It rests as a 64px strip of icons and opens to the same icons plus their names while the
 * pointer — or the keyboard — is on it. Nothing has to be pressed to get there: a rail that
 * opens on approach never needs a handle. Peeking is an overlay rather than a reflow — the
 * strip keeps its 64px of the layout and the open panel is drawn over the screen beneath it,
 * so nothing the user is reading moves sideways because the pointer passed the left edge.
 *
 * The pin is for the other way of working. Someone moving between agents all afternoon wants
 * the names on screen permanently, and hovering to read them every time is worse than losing
 * 172px. So pinning does the one thing peeking deliberately does not: it takes the open width
 * in the layout and moves the chat or agent screen right, out from under the panel, because a
 * rail that is staying must not be sitting on top of the content. Unpinning gives the space
 * straight back and returns the rail to opening on approach.
 *
 * It is headed by the GreenRoad mark — the one place the company's logo appears, because the
 * rail is the one piece of chrome every AI service shares — and it ends with the two things
 * that belong to the whole product rather than to any one service: Settings and the way out.
 *
 * It renders `GET /Agents/Mine` and has no hard-coded agent list — AskMila is always present
 * and becomes one entry among the agents rather than the destination.
 *
 * <b>Below `lg` the same rail is a drawer.</b> Everything about it that is a pointer's idiom —
 * resting as a strip, opening on approach, holding a place in the layout — is a desktop idiom:
 * there is no hover on a touch screen, and 64px of a 375px screen is a sixth of it. So it holds
 * no width there, rests off-screen at its open width with every name already on it, and is put on
 * screen by the one button the shell draws (AppShell). A press on an entry, on the scrim, or
 * Escape closes it again — the drawer is a visit, not a state to be in. `invisible` rather than a
 * bare transform while it is away, so nothing on it can be reached by the keyboard from the
 * screen it is not on.
 */
const AgentRail = ({
  activeKey,
  onSelect,
  onOpenSettings,
  onSignOut,
  isFromCentral = false,
  isSettingsHighlighted = false,
  /* Below `lg`: the rail is drawn over the screen rather than beside it. */
  isDrawer = false,
  isDrawerOpen = false,
  onDismiss,
}) => {
  const { agents } = useAgentAccess();

  /* Two independent reasons the panel can be open, kept apart on purpose. Collapsing them into
     one flag would mean unpinning while the pointer is still on the pin slams the panel shut
     under that pointer — and then the hover it is already inside reopens it. Peek is left
     alone by the toggle, so unpinning just hands control back to the pointer. */
  const [isPeeking, setIsPeeking] = useState(false);
  const [isPinned, setIsPinned] = useState(readPinned);

  /* The rule settings and sign-out bring along from the controls they replaced: not while a
     response is streaming. Same singleton every other surface subscribes to, so the rail and
     the chat's own controls grey out and come back in the same instant. */
  const [isStreaming, setIsStreaming] = useState(isMenuDisabled.state);

  useEffect(() => {
    const onChange = () => setIsStreaming(isMenuDisabled.state);
    isMenuDisabled.subscribe(onChange);
    return () => isMenuDisabled.unsubscribe(onChange);
  }, []);

  /* A drawer is never half open: it carries its names the moment it is on screen, because it was
     asked for rather than passed over. Peek and pin are the pointer's two ways of asking, and
     there is no pointer here. */
  const isOpen = isDrawer || isPinned || isPeeking;

  const togglePin = () => {
    const next = !isPinned;
    setIsPinned(next);
    writePinned(next);
  };

  /* Escape closes the drawer, as it closes every other overlay in the product. Not captured:
     a dialog opened from a rail entry has already closed the drawer behind it. */
  useEffect(() => {
    if (!isDrawer || !isDrawerOpen) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") onDismiss?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDrawer, isDrawerOpen, onDismiss]);

  return (
    // The gutter the rail holds in the shell's flex row, and the whole of the difference
    // between the three modes: pinned it is the open width, so `main` is laid out beside the
    // panel; peeking it stays 64px and the panel is drawn over `main` instead; a drawer holds
    // nothing at all, because the screen it is on has none to spare.
    <div
      data-testid="rail-gutter"
      className={clsx(
        "relative h-full shrink-0 transition-[width] duration-200 ease-out",
        isDrawer ? "w-0" : isPinned ? RAIL_OPEN_WIDTH : RAIL_CLOSED_WIDTH
      )}
    >
      {/* What the drawer is lying on. It is also the largest and easiest way to close it, which
          is what a drawer opened by mistake wants most. */}
      {isDrawer && isDrawerOpen && (
        <div
          aria-hidden="true"
          data-testid="rail-scrim"
          onClick={onDismiss}
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
        />
      )}

      <nav
        aria-label="AI services"
        data-testid="rail-nav"
        aria-hidden={isDrawer && !isDrawerOpen ? "true" : undefined}
        onMouseEnter={isDrawer ? undefined : () => setIsPeeking(true)}
        onMouseLeave={isDrawer ? undefined : () => setIsPeeking(false)}
        onFocus={isDrawer ? undefined : () => setIsPeeking(true)}
        /* Focus moving from one entry to the next fires a blur and then a focus; without this
           check the rail would shut and reopen on every arrow key. Only focus leaving the rail
           altogether closes it. */
        onBlur={(event) => {
          if (isDrawer) return;
          if (!event.currentTarget.contains(event.relatedTarget)) setIsPeeking(false);
        }}
        /* `z-40` is what lets the open panel sit over the main area instead of pushing it:
           above the content and its dropdowns (`z-30` and below), still under the modals and
           the tour (`z-50`, `z-[60]`), which cover the rail as they should. The rail's tooltip
           now lives inside that stacking context rather than escaping to the top of the page,
           which is fine at z-40 — everything that outranks it also covers the rail, so there
           is no hovering an entry while one of them is up. */
        className={clsx(
          "inset-y-0 left-0 z-40 flex flex-col border-r border-whitesmoke-500 bg-whitesmoke px-2.5 py-5",
          /* Never both `absolute` and `fixed`: Tailwind emits `.absolute` after `.fixed`, so the
             two on one element is the later rule winning whatever the order on the element. */
          isDrawer ? "fixed" : "absolute",
          isDrawer
            ? clsx(
                RAIL_OPEN_WIDTH,
                "max-w-[85vw] shadow-[8px_0_26px_-12px_#303C5459] transition-transform duration-200 ease-out",
                /* Away, and out of the tab order with it: a panel that is off the screen but
                   still focusable is a focus ring on nothing. */
                isDrawerOpen ? "translate-x-0" : "invisible -translate-x-full"
              )
            : clsx(
                "transition-[width,box-shadow] duration-200 ease-out",
                isOpen ? RAIL_OPEN_WIDTH : RAIL_CLOSED_WIDTH,
                /* The drop shadow is what says "this is lying on top of the page", so it is worn
                   only while peeking. Pinned, the panel occupies its own width and the border
                   alone divides it from the content, the way the rest of the app's chrome does. */
                isOpen && !isPinned && "shadow-[8px_0_26px_-12px_#303C5459]"
              )
        )}
      >
        {/* One instance for the whole rail.
            `delayHide` is what stops the flicker when the pointer crosses from one entry to
            the next: without it the tooltip hides the instant the pointer leaves an entry and
            shows again a moment later, so a slow diagonal across a boundary reads as two
            tooltips fighting. The hide is now deferred long enough for the next entry to take
            over instead. `offset` keeps the card clear of the rail so it can never sit under
            the pointer, which would be a hover loop of its own. */}
        <Tooltip
          id={TOOLTIP_ID}
          className="tooltip-ai-rail"
          delayShow={250}
          delayHide={140}
          offset={14}
          clickable={false}
        />

        {/* The head: the GreenRoad logo, and the pin.

            The logo used to sit in AskMila's own sidebar, which made it AskMila's; the rail is
            what every AI service shares, so the company's mark belongs here. Two cuts of it
            cross-fade as an entry's label does, each centred in the shape the rail has at the
            time: closed, the square "g" icon fills the 44px slot an entry's icon holds, the one
            thing left on the strip; open, the full greenroad wordmark sits on the centre line of
            the panel — the header row is the panel's content width inside its even padding, so
            the row's centre is the panel's.

            Laid out at the open width and clipped, as everything else on the rail is: the pin
            has to be in the same place in both states, or it would slide sideways under the
            very pointer travelling towards it. Closed, it is simply not on screen — the pin is
            something you reach for once the panel is already open. */}
        <div className="mb-2 flex h-11 items-center overflow-hidden">
          <div className={clsx("relative flex shrink-0 items-center justify-end", RAIL_CONTENT_WIDTH)}>
            {/* The icon carries the name in both states; the wordmark is the same name drawn
                again, so it stays out of the accessibility tree. */}
            <img
              src={greenRoadIcon}
              alt="GreenRoad"
              draggable={false}
              data-testid="rail-logo-icon"
              className={clsx(
                "absolute left-1.5 h-8 w-8 select-none rounded-md transition-opacity duration-200",
                isOpen ? "opacity-0" : "opacity-100"
              )}
            />
            <img
              src={greenRoadLogo}
              alt=""
              draggable={false}
              data-testid="rail-wordmark"
              className={clsx(
                "absolute left-1/2 h-6 w-auto -translate-x-1/2 select-none transition-opacity duration-200",
                isOpen ? "opacity-100" : "opacity-0"
              )}
            />

            {/* The same slot, and on a drawer it holds the other thing a panel's corner is for.
                There is nothing to pin when the rail holds no width — pinning it open would be
                pinning it over the screen — so the control there is the way out of it, beside
                the scrim and Escape. */}
            {isDrawer ? (
              <button
                type="button"
                data-testid="rail-close"
                onClick={onDismiss}
                aria-label="Close AI services menu"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-dimgray-400 transition-colors hover:bg-whitesmoke-600 hover:text-dark-blue-100"
              >
                <X size={16} strokeWidth={1.9} />
              </button>
            ) : (
              <button
                type="button"
                data-testid="rail-pin"
                onClick={togglePin}
                aria-pressed={isPinned}
                aria-label={isPinned ? "Unpin AI services menu" : "Pin AI services menu open"}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={escapeHtml(
                  isPinned
                    ? "Unpin the menu. It goes back to opening when you point at it, over the screen rather than beside it."
                    : "Pin the menu open. The screen moves right to make room for it."
                )}
                data-tooltip-place="right"
                /* Not reachable by tab or by pointer while the panel is closed: an invisible
                   control that can still be focused is a focus ring on nothing. */
                tabIndex={isOpen ? 0 : -1}
                className={clsx(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-xl transition-[opacity,color,background-color] duration-200",
                  isPinned
                    ? "text-dark-blue-100 hover:bg-whitesmoke-600"
                    : "text-dimgray-400 hover:bg-whitesmoke-600 hover:text-dark-blue-100",
                  isOpen ? "opacity-100" : "pointer-events-none opacity-0"
                )}
              >
                {isPinned ? (
                  <PinOff size={15} strokeWidth={1.9} />
                ) : (
                  <Pin size={15} strokeWidth={1.9} />
                )}
              </button>
            )}
          </div>
        </div>

        {/* No gap between entries: a 2px strip between two icons is a strip that belongs to
            neither, and crossing it made the tooltip hide and re-show. The rows are 44px and
            contiguous, so every pixel of the column belongs to exactly one entry. */}
        <div className="scrollbar-invisible flex min-h-0 flex-1 flex-col overflow-y-auto">
          <RailSection label="AI Services" isOpen={isOpen} className="mb-1.5" />

          <RailItem
            icon={MessagesSquare}
            label="AskMila"
            description="Ask anything about your fleet. The chat, for the questions no agent covers."
            testId="rail-askmila"
            isActive={activeKey === ASKMILA_KEY}
            isOpen={isOpen}
            onClick={() => onSelect(ASKMILA_KEY)}
          />

          {agents.length > 0 && <RailSection label="Agents" isOpen={isOpen} className="my-1.5" />}

          {agents.map((agent) => {
            const Icon = ICONS[agent.iconKey] || Bot;

            return (
              <RailItem
                key={agent.agentKey}
                icon={Icon}
                label={agent.displayName}
                description={agent.description}
                badge={agent.status === "Preview" ? "Preview" : null}
                testId={`rail-${agent.agentKey}`}
                isActive={activeKey === agent.agentKey}
                isOpen={isOpen}
                onClick={() => onSelect(agent.agentKey)}
              />
            );
          })}
        </div>

        {/* The foot of the rail — below the scroller, so however many agents pile up above it,
            these are always the last things in the column and always on screen.

            Settings first. The profile behind it ("what Mila knows about you": name, role,
            language, units) is one profile for the whole product — AskMila and every agent
            read the same one — so its door is on the chrome they all share rather than in
            AskMila's own sidebar, where it read as AskMila's alone and could not be reached
            from an agent's screen at all. An agent's *own* settings are a different thing and
            stay inside that agent's screen.

            Then the way out. It used to float over the top-right of every surface; now that
            the rail is the navigation, leaving is one more place you can go. "Central" when
            the session came from GreenRoad Central (the click returns there), "Sign out" when
            it did not — the old button said "Central" in both cases, which was a lie in one of
            them. */}
        <div className="shrink-0 pt-1.5">
          <span aria-hidden="true" className="mx-2 mb-1.5 block h-px bg-whitesmoke-500" />
          <RailItem
            icon={Settings}
            label="Settings"
            description="What Mila knows about you — your name, role, language and units. One profile, shared by AskMila and every agent."
            testId="rail-settings"
            isActive={false}
            isOpen={isOpen}
            onClick={onOpenSettings}
            disabled={isStreaming}
            isHighlighted={isSettingsHighlighted}
          />
          <RailItem
            icon={LogOut}
            label={isFromCentral ? "Central" : "Sign out"}
            description={
              isFromCentral
                ? "Back to GreenRoad Central. The conversation on screen is cleared."
                : "Sign out of AskMila. The conversation on screen is cleared."
            }
            testId="rail-signout"
            isActive={false}
            isOpen={isOpen}
            onClick={onSignOut}
            disabled={isStreaming}
          />
        </div>
      </nav>
    </div>
  );
};

export default AgentRail;
